/**
 * Centralised RBAC (role-based access control) helper for the frontend.
 *
 * The backend is the source of truth — every API endpoint enforces firm
 * scoping and role checks server-side. These helpers exist to:
 *
 *   1. Hide UI that the user has no permission to use.
 *   2. Prevent a user from reaching a sensitive page directly via URL
 *      (e.g. typing /accounting/general-ledger as a paralegal).
 *
 * Bypassing this layer is not a security issue — the backend will still
 * reject the request. The frontend gate is purely a UX guard.
 */

import { ROLES } from './roles';

const SUPERUSER = ROLES.SUPERUSER;
const PARTNER = ROLES.PARTNER;
const ACCOUNTANT = 'ACCOUNTANT';
const ASSOCIATE = ROLES.ASSOCIATE;
const PARALEGAL = ROLES.PARALEGAL;
const SUPPORT_STAFF = ROLES.SUPPORT_STAFF;
const BOARD_MEMBER = ROLES.BOARD_MEMBER;
const AUDITOR = 'AUDITOR';

const ALL_STAFF = [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL, SUPPORT_STAFF];

/**
 * tabId → list of roles that may access it.
 *
 * Every real sidebar nav item id has an explicit entry here (closed by
 * default - see canAccessTab below). This used to be a partial list where
 * an absent tabId meant "open to all staff", which let ACCOUNTANT and
 * BOARD_MEMBER (both allowlist-style roles in App.js's old per-role sidebar
 * arrays) silently gain access to anything not yet added here. The
 * consolidation that made this the single source of truth for both sidebar
 * and content-area gating (frontend/src/App.js's filterItem now calls
 * canAccessTab directly instead of maintaining its own per-role arrays)
 * required making every tab explicit first, precisely to avoid that
 * silent-widening trap.
 */
export const TAB_ACCESS = {
  // ── Practice management — open to every case-facing staff role ──
  dashboard:              [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL, SUPPORT_STAFF, BOARD_MEMBER],
  clients:                [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  cases:                  [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  documents:              [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  clauseLibrary:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  tasks:                  [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  taskTemplates:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  calendar:               [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  unlinkedInbox:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  emailRules:             [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  communicationLogs:      [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  timeExpense:            [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  activityLog:            [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  myPerformance:          [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  performanceReviews:     [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  conveyancingDashboard:  [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  properties:             [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  bankInstructionInbox:   [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  conveyancingBatches:    [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  conveyancingReports:    [SUPERUSER, PARTNER, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],
  // Integrations (Outlook/email) is open to every staff role today, same as
  // it was before this consolidation - flagged in
  // training-docs/_meta/permission-audit-findings.md as worth a deliberate
  // product decision, not changed here.
  integrations:           [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL, SUPPORT_STAFF],

  // ── Accounting & finance ─ accountants + partners only ──
  chartOfAccounts:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  generalLedger:          [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  suppliers:              [SUPERUSER, PARTNER, ACCOUNTANT],
  creditors:              [SUPERUSER, PARTNER, ACCOUNTANT],
  pettyCash:              [SUPERUSER, PARTNER, ACCOUNTANT],
  accountingReview:       [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  firmExpenses:           [SUPERUSER, PARTNER, ACCOUNTANT],
  bankAccounts:           [SUPERUSER, PARTNER, ACCOUNTANT],
  bankReconciliation:     [SUPERUSER, PARTNER, ACCOUNTANT],
  vatReturns:             [SUPERUSER, PARTNER, ACCOUNTANT],
  trialBalance:           [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  balanceSheet:           [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  incomeStatement:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  cashFlow:               [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
  accountingPeriods:      [SUPERUSER, PARTNER, ACCOUNTANT],
  trustAccounting:        [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],

  // ── Admin & sensitive settings ──
  manageAuditors:         [SUPERUSER],
  // ACCOUNTANT added 2026-07-24: the sidebar's old per-role array already
  // showed this tab to Accountants, and FirmSettingsView's backend write
  // guard already allows Partner or Accountant - this list was the stale
  // one, denying the tab after the nav link was already shown.
  firmSettings:           [SUPERUSER, PARTNER, ACCOUNTANT],
  payrollManagement:      [SUPERUSER, PARTNER, ACCOUNTANT],
  teamOverview:           [SUPERUSER, PARTNER, BOARD_MEMBER],
  departments:            [SUPERUSER, PARTNER],
  // ACCOUNTANT is limited to invoices/payments/expenses by an in-method
  // entity allow-list in ImportViewSet.import_csv. [H11]
  imports:                [SUPERUSER, PARTNER, PARALEGAL, SUPPORT_STAFF, ACCOUNTANT],
  security:               ALL_STAFF.concat(BOARD_MEMBER),

  // ── Reports — staff who can see financials ──
  // ASSOCIATE added 2026-07-24: same "sidebar showed it, this list denied
  // it" pattern as firmSettings above.
  reports:                [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, BOARD_MEMBER, AUDITOR],
  insightsReports:        [SUPERUSER, PARTNER, ACCOUNTANT, BOARD_MEMBER],

  // ── Dual-approval workflow — attorneys who can sign off on paralegal work ──
  pendingApprovals:       [SUPERUSER, PARTNER, ASSOCIATE],

  // ── Billing / collections ──
  billing:                [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL],
  debtCollectionDashboard: [SUPERUSER, PARTNER, ACCOUNTANT],
  collectionsSettings:    [SUPERUSER, PARTNER, ACCOUNTANT],
  collectionsWorklist:    [SUPERUSER, PARTNER, ACCOUNTANT, SUPPORT_STAFF],
  billsOfCosts:           [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE],
  // NOTE: ASSOCIATE and PARALEGAL were previously excluded here despite
  // ASSOCIATE already having `canManage` rights in ServiceProductManagement.js
  // — the tab itself was invisible to them (same "check all three gates
  // independently" pattern documented in CLAUDE.md for SUPPORT_STAFF/Imports.
  serviceProducts:        [SUPERUSER, PARTNER, ACCOUNTANT, ASSOCIATE, PARALEGAL],

  // ── Compliance & audit ──
  auditManagement:        [SUPERUSER, PARTNER, AUDITOR],
  complianceHub:          [SUPERUSER, PARTNER, ACCOUNTANT],
  attorneyRecords:        [SUPERUSER, PARTNER, ACCOUNTANT],
  annualCertificateManagement: [SUPERUSER, PARTNER, ACCOUNTANT, AUDITOR],
};

/**
 * Returns true if the user role can access the given tab.
 * Superusers always get access. Unknown tabs fail closed (denied) - every
 * real tab id is listed above; an unlisted id means either a typo or a new
 * tab that hasn't been added to TAB_ACCESS yet, and the safe default for
 * either case is to hide it, not show it.
 */
export function canAccessTab(userRole, tabId, currentUser = null) {
  if (currentUser && currentUser.is_superuser) return true;
  if (userRole === SUPERUSER) return true;
  const allowed = TAB_ACCESS[tabId];
  if (!allowed) return false; // Tab not gated - fail closed
  return allowed.includes(userRole);
}

/**
 * Get the list of tabs the current user is NOT allowed to access.
 * Useful for redirecting away from blocked URLs.
 */
export function blockedTabsFor(userRole, currentUser = null) {
  if (currentUser && currentUser.is_superuser) return [];
  if (userRole === SUPERUSER) return [];
  return Object.keys(TAB_ACCESS).filter(t => !canAccessTab(userRole, t, currentUser));
}
